import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  Tooltip,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  AutoFixHigh as AutoFixHighIcon,
  Message as MessageIcon,
  Mail as MailIcon,
  Phone as PhoneIcon,
  Public as PublicIcon,
  Business as BusinessIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import MessageTemplateService from '../../../services/messageTemplateService';
import { api } from '../../../services/authService';
import CreateTemplateDialog from '../../../components/templates/CreateTemplateDialog';
import EditTemplateDialog from '../../../components/templates/EditTemplateDialog';

// Constants from Django model
const CHANNEL_CHOICES = {
  sms: 'SMS',
  email: 'Email',
  voice: 'Voice',
  chat: 'Chat',
};

const CATEGORY_CHOICES = {
  awareness: 'Awareness',
  interest: 'Interest',
  decision: 'Decision',
  conversion: 'Conversion',
  lost: 'Lost',
};

// Channel icon mapping
const ChannelIcon = ({ channel }) => {
  switch (channel) {
    case "sms":
      return <MessageIcon fontSize="small" />;
    case "email":
      return <MailIcon fontSize="small" />;
    case "voice":
      return <PhoneIcon fontSize="small" />;
    case "chat":
      return <MessageIcon fontSize="small" />;
    default:
      return <MessageIcon fontSize="small" />;
  }
};

const ConversationsTemplates = () => {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [templates, setTemplates] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedChannel, setSelectedChannel] = useState("all");
  const [templateScope, setTemplateScope] = useState("all");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState("grid");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [availableVariables, setAvailableVariables] = useState({});
  const [newTemplate, setNewTemplate] = useState({
    name: "",
    content: "",
    category: "awareness",
    channel: "sms",
    is_system_template: false,
    account: '',
  });
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const fetchAccounts = async () => {
    try {
      const response = await api.get('/crm/accounts/');
      setAccounts(response.data || []);
      if (response.data && response.data.length > 0) {
        setSelectedAccount(response.data[0].id);
        setNewTemplate(prev => ({
          ...prev,
          account: response.data[0].id,
        }));
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
      setAccounts([]);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  useEffect(() => {
    if (selectedAccount) {
      fetchTemplates();
      fetchAvailableVariables();
    }
  }, [selectedCategory, selectedChannel, templateScope, selectedAccount]);

  const fetchAvailableVariables = async () => {
    try {
      const variables = await MessageTemplateService.getAvailableVariables();
      setAvailableVariables(variables);
    } catch (err) {
      console.error('Failed to fetch available variables:', err);
    }
  };

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const filters = {
        category: selectedCategory !== 'all' ? selectedCategory : undefined,
        channel: selectedChannel !== 'all' ? selectedChannel : undefined,
        scope: templateScope,
        account: templateScope === 'account' ? selectedAccount : undefined,
      };
      const data = await MessageTemplateService.getTemplates(filters);
      setTemplates(data);
    } catch (err) {
      setError('Failed to fetch templates');
      showSnackbar('Failed to fetch templates', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAccountChange = (event) => {
    setSelectedAccount(event.target.value);
    setNewTemplate(prev => ({
      ...prev,
      account: event.target.value,
    }));
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const filteredTemplates = templates.filter((template) => {
    const matchesSearch =
      template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.content.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const handleCreateTemplate = async () => {
    try {
      setLoading(true);
      const templateData = {
        ...newTemplate,
        account: templateScope === 'account' ? selectedAccount : null,
      };
      const savedTemplate = await MessageTemplateService.createTemplate(templateData);
      setTemplates([...templates, savedTemplate]);
      setIsCreateDialogOpen(false);
      setNewTemplate({
        name: "",
        content: "",
        category: "awareness",
        channel: "sms",
        is_system_template: false,
        account: selectedAccount,
      });
      showSnackbar('Template created successfully');
    } catch (err) {
      setError('Failed to create template');
      showSnackbar('Failed to create template', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async () => {
    if (!selectedTemplate) return;
    try {
      setLoading(true);
      await MessageTemplateService.deleteTemplate(selectedTemplate.id);
      setTemplates(templates.filter(t => t.id !== selectedTemplate.id));
      handleMenuClose();
      showSnackbar('Template deleted successfully');
    } catch (err) {
      setError('Failed to delete template');
      showSnackbar('Failed to delete template', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateTemplate = async () => {
    if (!selectedTemplate) return;
    try {
      setLoading(true);
      const duplicateData = {
        ...selectedTemplate,
        name: `${selectedTemplate.name} (Copy)`,
        is_system_template: false,
        account: selectedAccount,
      };
      delete duplicateData.id;
      const newTemplate = await MessageTemplateService.createTemplate(duplicateData);
      setTemplates([...templates, newTemplate]);
      handleMenuClose();
      showSnackbar('Template duplicated successfully');
    } catch (err) {
      setError('Failed to duplicate template');
      showSnackbar('Failed to duplicate template', 'error');
    } finally {
      setLoading(false);
    }
  };

  const generateAIContent = async () => {
    try {
      setLoading(true);
      const previewData = {
        content: newTemplate.content,
        context: {
          lead: {
            first_name: 'John',
            last_name: 'Doe',
            email: 'john@example.com'
          }
        }
      };
      const result = await MessageTemplateService.previewTemplate(previewData);
      setNewTemplate({
        ...newTemplate,
        content: result.preview
      });
    } catch (err) {
      setError('Failed to generate AI content');
      showSnackbar('Failed to generate AI content', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleMenuClick = (event, template) => {
    setAnchorEl(event.currentTarget);
    setSelectedTemplate(template);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    if (!isEditDialogOpen) {
      setSelectedTemplate(null);
    }
  };

  const getTemplateScopeChip = (template) => {
    if (template.is_system_template) {
      return (
        <Chip
          icon={<PublicIcon />}
          label="System"
          size="small"
          color="primary"
          variant="outlined"
        />
      );
    }
    return (
      <Chip
        icon={<BusinessIcon />}
        label="Account"
        size="small"
        color="secondary"
        variant="outlined"
      />
    );
  };

  const renderVariableList = () => {
    return Object.entries(availableVariables).map(([category, variables]) => (
      <Box key={category} sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {category.charAt(0).toUpperCase() + category.slice(1)} Variables
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {Object.entries(variables).map(([name, info]) => (
            <Tooltip key={name} title={info.description}>
              <Chip
                label={`{{${category}.${name}}}`}
                size="small"
                onClick={() => {
                  const variable = `{{${category}.${name}}}`;
                  setNewTemplate(prev => ({
                    ...prev,
                    content: prev.content + variable
                  }));
                }}
              />
            </Tooltip>
          ))}
        </Box>
      </Box>
    ));
  };

  const handleTemplateCreated = (newTemplate) => {
    setTemplates([...templates, newTemplate]);
  };

  const handleEditTemplate = () => {
    setIsEditDialogOpen(true);
    setAnchorEl(null);
  };

  const handleTemplateUpdated = (updatedTemplate) => {
    setTemplates(templates.map(t => 
      t.id === updatedTemplate.id ? updatedTemplate : t
    ));
  };

  const handleUseTemplate = () => {
    if (selectedTemplate) {
      navigator.clipboard.writeText(selectedTemplate.content)
        .then(() => {
          showSnackbar('Template content copied to clipboard', 'success');
        })
        .catch((err) => {
          console.error('Failed to copy template:', err);
          showSnackbar('Failed to copy template content', 'error');
        });
    }
    handleMenuClose();
  };

  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1">
          Conversation Templates
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setIsCreateDialogOpen(true)}
          disabled={loading}
        >
          New Template
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <TextField
          placeholder="Search templates..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1 }}
        />
        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel>Account</InputLabel>
          <Select
            value={selectedAccount}
            onChange={handleAccountChange}
            label="Account"
          >
            {accounts.map((account) => (
              <MenuItem key={account.id} value={account.id}>
                {account.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Select
          value={templateScope}
          onChange={(e) => setTemplateScope(e.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="all">All Templates</MenuItem>
          <MenuItem value="system">System Templates</MenuItem>
          <MenuItem value="account">Account Templates</MenuItem>
        </Select>
        <Select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="all">All Categories</MenuItem>
          {Object.entries(CATEGORY_CHOICES).map(([value, label]) => (
            <MenuItem key={value} value={value}>{label}</MenuItem>
          ))}
        </Select>
        <Select
          value={selectedChannel}
          onChange={(e) => setSelectedChannel(e.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="all">All Channels</MenuItem>
          {Object.entries(CHANNEL_CHOICES).map(([value, label]) => (
            <MenuItem key={value} value={value}>{label}</MenuItem>
          ))}
        </Select>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={viewMode} onChange={(e, newValue) => setViewMode(newValue)}>
          <Tab label="Grid View" value="grid" />
          <Tab label="List View" value="list" />
        </Tabs>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredTemplates.length === 0 ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          mt: 4,
          p: 4,
          textAlign: 'center'
        }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No templates found
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {templateScope === 'account' 
              ? `No templates have been created for this account yet.`
              : 'No templates match your current filters.'}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setIsCreateDialogOpen(true)}
          >
            Create Template
          </Button>
        </Box>
      ) : viewMode === "grid" ? (
        <Grid container spacing={3}>
          {filteredTemplates.map((template) => (
            <Grid item xs={12} sm={6} md={4} key={template.id}>
              <Card>
                <CardHeader
                  title={template.name}
                  subheader={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <ChannelIcon channel={template.channel} />
                      <Typography variant="body2" color="text.secondary">
                        {CATEGORY_CHOICES[template.category]}
                      </Typography>
                      {getTemplateScopeChip(template)}
                    </Box>
                  }
                  action={
                    <IconButton onClick={(e) => handleMenuClick(e, template)}>
                      <MoreVertIcon />
                    </IconButton>
                  }
                />
                <CardContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {template.content}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Created: {new Date(template.created_at).toLocaleDateString()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Paper>
          <Box sx={{ p: 2 }}>
            <Grid container spacing={2} sx={{ fontWeight: 'bold' }}>
              <Grid item xs={3}>Name</Grid>
              <Grid item xs={3}>Category</Grid>
              <Grid item xs={2}>Channel</Grid>
              <Grid item xs={2}>Scope</Grid>
              <Grid item xs={1}>Created</Grid>
              <Grid item xs={1}>Actions</Grid>
            </Grid>
            <Divider sx={{ my: 1 }} />
            {filteredTemplates.map((template) => (
              <React.Fragment key={template.id}>
                <Grid container spacing={2} alignItems="center" sx={{ p: 2, '&:hover': { bgcolor: 'action.hover' } }}>
                  <Grid item xs={3}>{template.name}</Grid>
                  <Grid item xs={3}>{CATEGORY_CHOICES[template.category]}</Grid>
                  <Grid item xs={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ChannelIcon channel={template.channel} />
                      <Typography variant="body2">{CHANNEL_CHOICES[template.channel]}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={2}>
                    {getTemplateScopeChip(template)}
                  </Grid>
                  <Grid item xs={1}>{new Date(template.created_at).toLocaleDateString()}</Grid>
                  <Grid item xs={1}>
                    <IconButton 
                      onClick={(e) => handleMenuClick(e, template)}
                      size="small"
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Grid>
                </Grid>
                <Divider />
              </React.Fragment>
            ))}
          </Box>
        </Paper>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {selectedTemplate && (
          <>
            {!selectedTemplate.is_system_template && (
              <MenuItem onClick={handleEditTemplate}>
                <EditIcon sx={{ mr: 1 }} /> Edit
              </MenuItem>
            )}
            <MenuItem onClick={handleDuplicateTemplate}>
              <CopyIcon sx={{ mr: 1 }} /> Duplicate
            </MenuItem>
            <MenuItem onClick={handleUseTemplate}>
              <CopyIcon sx={{ mr: 1 }} /> Use
            </MenuItem>
            {!selectedTemplate.is_system_template && (
              <>
                <Divider />
                <MenuItem onClick={handleDeleteTemplate} sx={{ color: 'error.main' }}>
                  <DeleteIcon sx={{ mr: 1 }} /> Delete
                </MenuItem>
              </>
            )}
          </>
        )}
      </Menu>

      <CreateTemplateDialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        accounts={accounts}
        selectedAccount={selectedAccount}
        onTemplateCreated={handleTemplateCreated}
        loading={loading}
        setLoading={setLoading}
        showSnackbar={showSnackbar}
      />

      <EditTemplateDialog
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        template={selectedTemplate}
        onTemplateUpdated={handleTemplateUpdated}
        loading={loading}
        setLoading={setLoading}
        showSnackbar={showSnackbar}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ConversationsTemplates; 